import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, from } from 'rxjs';
import { CanadianWorkersCompensationForm } from 'src/app/classes/form-groups/canadian-workers-compensation-form';
import {
  Address,
  CanadianWorkersCompensation,
  Company,
  CompanyVendor,
  Contact,
  Note,
  User,
  Vendor,
  ViveProgress
} from 'src/app/classes/models';
import { TicketUrl } from 'src/app/classes/ticket-url';
import { CanadianWorkersCompensationType } from 'src/app/interfaces/canadian-workers-compensation-type';
import { ComplianceSteps } from 'src/app/interfaces/compliance-steps';
import { State } from 'src/app/interfaces/state';
import { UserAccountRoles } from 'src/app/interfaces/user-account-roles';
import { AddEditViveProgressModalComponent } from 'src/app/modals/add-edit-vive-progress-modal/add-edit-vive-progress-modal.component';
import { CanadianWorkersCompensationModalComponent } from 'src/app/modals/canadian-workers-compensation-modal/canadian-workers-compensation-modal.component';
import { ComplianceNoteModalComponent } from 'src/app/modals/compliance-note-modal/compliance-note-modal.component';
import { CreateTicketModalComponent } from 'src/app/modals/create-ticket-modal/create-ticket-modal.component';
import { NoteModalComponent } from 'src/app/modals/note-modal/note-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { FileService } from 'src/app/services/file.service';
import { NoteService } from 'src/app/services/note.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { VendorService } from 'src/app/services/vendor.service';

@Component({
  selector: 'app-client-vendor-compliance-info',
  templateUrl: './client-vendor-compliance-info.component.html',
  styleUrls: ['./client-vendor-compliance-info.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ClientVendorComplianceInfoComponent implements OnInit {
  constructor(
    private ModalService: NgbModal,
    private ToastService: ToastService,
    private CompanyVendorService: CompanyVendorService,
    private NoteService: NoteService,
    private FileService: FileService,
    private SharedDataService: SharedDataService,
    private AuthService: AuthService,
    private VendorService: VendorService,
    private UtilityService: UtilityService,
    private cdr: ChangeDetectorRef
  ) {
    this.LoggedInUser = this.AuthService.getUser();

    const { UserAccountRole: { role } = {} } = this.LoggedInUser;

    if (
      role &&
      new Set([...this.fullSSNPermissions, ...this.maskedSSNPermissions]).has(
        role
      )
    ) {
      this.hasSSNAccess = true;
    }
  }

  @Input() CompanyVendor!: CompanyVendor;
  @Input() CompanyId?: number;
  @Input() VendorId?: number;
  @Input() SharedOperationStates!: Set<State>;
  @Input() CurrentComplianceStep?: ComplianceSteps;
  @Input() CurrentComplianceProgress?: ViveProgress;

  Company!: Company;
  Vendor!: Vendor;
  StreetAddress?: Address;
  MailingAddress?: Address;
  RemitToAddress?: Address;
  ComplianceContact?: Contact;
  LoggedInUser: User;
  maskedSSN?: string;
  fullSSN?: string;
  hasSSNAccess: boolean = false;

  fullSSNPermissions = [
    'ADMIN_ADMIN',
    'ADMIN_FRAUD_TEAM',
    'ADMIN_PROJECT_MANAGEMENT_TEAM'
  ];

  maskedSSNPermissions = [
    'ADMIN_CUSTOMER_SERVICE_TEAM',
    'ADMIN_INSURANCE_TEAM'
  ];

  CanadianWorkersCompensationTypes: CanadianWorkersCompensationType[] = [];

  ComplianceStepToTicketTypeMap = {
    'Vendor Info': 'vendorInfoReview',
    'Owner Info': 'ownerInfoReview',
    'Trades & Licenses': 'tradeLicensesReview',
    Insurance: 'insuranceReview'
  };

  // VPDW-4240: the widest of the backend's info-renewal reminder tiers, which
  // fire 60 / 30 / 7 days before the due date
  // (vive-v2 communications/vendor-info-renewal-upcoming-routing.ts). Reusing
  // that number rather than inventing a display threshold means the chip turns
  // amber on the same day the vendor gets their first reminder.
  private static readonly INFO_RENEWAL_DUE_SOON_DAYS = 60;

  /**
   * True when the vendor has never completed an information renewal, so the due
   * date is derived from `Vendor.createdAt` rather than a real renewal. The date
   * is still authoritative — the suspension cron uses the same anchor — but a
   * reviewer would otherwise read it as "they renewed a year ago".
   */
  public get infoRenewalIsAnchoredOnSignup(): boolean {
    return (
      !!this.CompanyVendor?.infoRenewalDueDate && !this.Vendor?.renewalDate
    );
  }

  /**
   * Whole days from now until the information renewal is due; negative once it
   * is overdue. Null when there is no date to compare against.
   */
  private get infoRenewalDaysUntilDue(): number | null {
    const dueDate = this.CompanyVendor?.infoRenewalDueDate;

    if (!dueDate) {
      return null;
    }

    // Compare calendar days, not instants — otherwise a date due later today
    // reads as overdue, and "Due in 1 day" flickers by wall-clock time.
    const due = new Date(dueDate);
    const startOfDueDate = new Date(
      due.getFullYear(),
      due.getMonth(),
      due.getDate()
    );
    const now = new Date();
    const startOfToday = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );

    return Math.round(
      (startOfDueDate.getTime() - startOfToday.getTime()) / 86400000
    );
  }

  /**
   * Chip text for the information renewal, or null when the vendor is current.
   * Nothing is rendered in the healthy case on purpose — docs/design-components.md
   * says not to pill a passing state.
   */
  public get infoRenewalChipLabel(): string | null {
    const days = this.infoRenewalDaysUntilDue;

    if (days === null) {
      return null;
    }

    if (days < 0) {
      const overdueBy = Math.abs(days);

      return `Overdue ${overdueBy} ${overdueBy === 1 ? 'day' : 'days'}`;
    }

    if (days === 0) {
      return 'Due today';
    }

    if (
      days <= ClientVendorComplianceInfoComponent.INFO_RENEWAL_DUE_SOON_DAYS
    ) {
      return `Due in ${days} ${days === 1 ? 'day' : 'days'}`;
    }

    return null;
  }

  public get infoRenewalChipClass(): string {
    const days = this.infoRenewalDaysUntilDue;

    return days !== null && days < 0 ? 'chip-error' : 'chip-warning';
  }

  public get reSignRequired(): boolean {
    return (this.CompanyVendor?.resignRequired ?? null) !== null;
  }

  public get reSignRequiredDate(): Date | null {
    return this.CompanyVendor?.resignRequired ?? null;
  }

  // Returns a boolean value to determine if the current VIVE Admin user can set the
  // step status.
  AllowedSetStepStatusUserRoles() {
    if (this.CurrentComplianceStep) {
      const { UserAccountRole: { role } = {} } = this.LoggedInUser;

      if (!role) {
        throw new Error('Missing user permission role.');
      }

      const UserRoleSet: Set<UserAccountRoles> = new Set([
        'ADMIN_ADMIN',
        // 'ADMIN_EMAILS_TEAM',
        'ADMIN_PROJECT_MANAGEMENT_TEAM'
      ]);

      if (this.CurrentComplianceStep === 'Insurance') {
        UserRoleSet.add('ADMIN_INSURANCE_TEAM');
        UserRoleSet.add('ADMIN_PRIME_TEAM');
      } else {
        UserRoleSet.add('ADMIN_FRAUD_TEAM');
      }

      return UserRoleSet.has(role);
    }

    return false;
  }

  // Update Client/Vendor record isWorkersCompExempt status.
  updateWorkersCompExempt() {
    const { id, CompanyId, VendorId, isWorkersCompExempt } = this.CompanyVendor;

    const CompanyVendor = Object.assign(
      { id, CompanyId, VendorId },
      {
        isWorkersCompExempt: !isWorkersCompExempt
      }
    );

    this.CompanyVendorService.saveCompanyVendor(CompanyVendor).subscribe({
      next: () => {
        Object.assign(this.CompanyVendor, {
          isWorkersCompExempt: !isWorkersCompExempt
        });
        this.SharedDataService.sendEvent('ReloadComplianceResults');
        this.ToastService.addSuccessToast(
          'Success',
          "Successfully updated worker's comp. exempt status."
        );
        this.cdr.markForCheck();
      }
    });
  }

  setWorkersCompExemptValidity(value: boolean) {
    const { id, CompanyId, VendorId } = this.CompanyVendor;

    const CompanyVendor = Object.assign(
      { id, CompanyId, VendorId },
      {
        isWCExemptValid: value
      }
    );

    this.CompanyVendorService.saveCompanyVendor(CompanyVendor).subscribe({
      next: () => {
        Object.assign(this.CompanyVendor, {
          isWCExemptValid: CompanyVendor.isWCExemptValid
        });
        this.SharedDataService.sendEvent('ReloadComplianceResults');
        this.ToastService.addSuccessToast(
          'Success',
          `Worker\'s Comp. Exempt is ${value ? 'Passed' : 'Failed'}`
        );
        this.cdr.markForCheck();
      }
    });
  }

  setStepStatus() {
    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Client Id.', {
        returnError: true
      });
    }

    if (!this.VendorId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
        returnError: true
      });
    }

    const SharedDataServiceSubscription =
      this.SharedDataService.listenForEvent<boolean>().subscribe({
        next: ({ eventName, data }) => {
          if (
            eventName === 'RespondIfPassedFindings' &&
            this.CurrentComplianceStep
          ) {
            const { componentInstance, result } = this.ModalService.open(
              AddEditViveProgressModalComponent,
              { backdrop: false, size: 'md' }
            );

            const { id: CompanyVendorId } = this.CompanyVendor;

            Object.assign(componentInstance, {
              CompanyVendorId,
              CompanyId: this.CompanyId,
              VendorId: this.VendorId,
              complianceStep: this.CurrentComplianceStep,
              isStepInPassingState: data,
              ViveProgress: this.CurrentComplianceProgress
            });

            from(result).subscribe({
              next: (ComplianceProgress) => {
                this.SharedDataService.sendEvent(
                  'SetUpdatedComplianceProgress',
                  new ViveProgress(ComplianceProgress)
                );
                this.loadCompanyVendorData();
                this.ToastService.addSuccessToast(
                  'Success',
                  'Successfully updated compliance progress.'
                );
              }
            });
          }

          SharedDataServiceSubscription.unsubscribe();
        }
      });
    this.SharedDataService.sendEvent('CheckForPassedFindings');
  }

  createTicket() {
    if (!this.CompanyVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot create ticket. Missing Client/Vendor info.',
        { returnError: true }
      );
    }

    const { Vendor } = this.CompanyVendor;

    if (!Vendor || (Vendor && !Vendor.ComplianceContact)) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot create ticket. Missing vendor contact info.',
        { returnError: true }
      );
    }

    if (!this.CurrentComplianceStep) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot create ticket. Missing current compliance step.',
        { returnError: true }
      );
    }

    const CreateTicketModal = this.ModalService.open(
      CreateTicketModalComponent,
      { backdrop: 'static', modalDialogClass: 'create-ticket-modal' }
    );
    const { componentInstance } = CreateTicketModal;

    Object.assign(componentInstance, {
      CompanyVendor: this.CompanyVendor,
      CompanyId: this.CompanyId,
      VendorId: this.VendorId,
      ticketType:
        this.ComplianceStepToTicketTypeMap[
          this.CurrentComplianceStep as Exclude<ComplianceSteps, 'Final Review'>
        ]
    });

    const observable = from(CreateTicketModal.result) as Observable<string>;
    observable.subscribe({
      next: (url) => {
        this.SharedDataService.sendEvent(
          'ReloadClientVendorDataOpenTicket',
          new TicketUrl({ url })
        );
      }
    });
  }

  createComplianceNote() {
    if (!this.CurrentComplianceStep) {
      const NoteModal = this.ModalService.open(NoteModalComponent, {
        backdrop: 'static',
        modalDialogClass: 'note-modal'
      });

      Object.assign(NoteModal.componentInstance, {
        CompanyId: this.CompanyId,
        VendorId: this.VendorId
      });

      from(NoteModal.result).subscribe({
        next: ({ Note: note }: { Note: Note }) => {
          this.NoteService.saveNote(note).subscribe({
            next: () => {
              this.ToastService.addSuccessToast(
                'Success',
                'Successfully saved note.'
              );
            }
          });
        }
      });

      return;
    }

    const NoteModal = this.ModalService.open(ComplianceNoteModalComponent, {
      backdrop: 'static',
      modalDialogClass: 'note-modal'
    });

    const { componentInstance } = NoteModal;

    componentInstance.CompanyId = this.CompanyId;
    componentInstance.VendorId = this.VendorId;
    componentInstance.CurrentComplianceStep = this.CurrentComplianceStep;

    from(NoteModal.result).subscribe({
      next: (Note) => {
        this.NoteService.saveNote(Note).subscribe({
          next: (Note) => {
            // Note saved successfully.
            this.ToastService.addSuccessToast(
              'Success',
              'Successfully saved note.'
            );
          }
        });
      }
    });
  }

  displayCAWCModal(
    CanadianWorkersCompensationType: CanadianWorkersCompensationType
  ) {
    const {
      id: CompanyVendorId,
      CompanyId,
      VendorId,
      [CanadianWorkersCompensationType]: CanadianWorkersCompensation
    } = this.CompanyVendor;

    this.FileService.getCompanyVendorFile({
      CompanyVendorId: CompanyVendorId,
      CompanyId: CompanyId,
      VendorId: VendorId,
      docType: 'WSIB',
      order: JSON.stringify([['createdAt', 'DESC']])
    }).subscribe({
      next: (CompanyVendorFile) => {
        const { componentInstance, result } = this.ModalService.open(
          CanadianWorkersCompensationModalComponent,
          { backdrop: 'static', size: 'lg' }
        );

        const CanadianWorkersCompensationFormGroup =
          new CanadianWorkersCompensationForm();

        CanadianWorkersCompensationFormGroup.patchValue(
          CanadianWorkersCompensation || {
            CompanyVendorId,
            CompanyId: CompanyId || undefined,
            VendorId: VendorId || undefined
          }
        );

        Object.assign(componentInstance, {
          CanadianWorkersCompensationFormGroup,
          CompanyVendorId,
          CompanyId,
          VendorId,
          CanadianWorkersCompensationType,
          WSIBCertificateImageFile: CompanyVendorFile || undefined
        });

        from(result).subscribe({
          next: (
            SavedCanadianWorkersCompensation: CanadianWorkersCompensation
          ) => {
            Object.assign(this.CompanyVendor, {
              [CanadianWorkersCompensationType]:
                SavedCanadianWorkersCompensation
            });
            this.SharedDataService.sendEvent('ReloadCompanyVendorData');
          }
        });
      }
    });
  }

  loadCompanyVendorData() {
    this.CompanyVendorService.getCompanyVendor({
      aliasedAttributes: JSON.stringify([
        'hasMultipleClients',
        'vendorRiskLevel',
        'lastPaymentDate',
        // VPDW-4240: both renewal clocks. This self-fetch fallback omitted
        // `renewalDate` altogether, so whenever it ran instead of the parent
        // supplying the CompanyVendor the renewal row rendered '—' — the same
        // root cause as the missing info-renewal date, so both land here.
        'renewalDate',
        'infoRenewalDueDate'
      ]),
      CompanyId: this.CompanyId,
      VendorId: this.VendorId,
      Vendor: JSON.stringify({
        aliasedAttributes: ['hasSSN'],
        ComplianceContact: {},
        StreetAddress: {
          options: { required: false },
          attributes: ['id', 'address1', 'address2', 'city', 'state', 'zip']
        },
        MailingAddress: {
          options: { required: false },
          attributes: ['id', 'address1', 'address2', 'city', 'state', 'zip']
        },
        RemitToAddress: {
          options: { required: false },
          attributes: ['id', 'address1', 'address2', 'city', 'state', 'zip']
        }
      }),
      CompanyComplianceType: JSON.stringify({}),
      Company: JSON.stringify({})
    }).subscribe({
      next: (CompanyVendor) => {
        if (!CompanyVendor) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Client/Vendor information.',
            { returnError: true }
          );
        }

        const { Company, Vendor } = CompanyVendor;

        if (!Company) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Client information.',
            { returnError: true }
          );
        }

        if (!Vendor) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Vendor information.',
            { returnError: true }
          );
        }

        const {
          ComplianceContact,
          StreetAddress,
          MailingAddress,
          RemitToAddress
        } = Vendor;

        if (ComplianceContact) {
          this.ComplianceContact = ComplianceContact;
        }

        if (StreetAddress) {
          this.StreetAddress = StreetAddress;
        }

        if (MailingAddress) {
          this.MailingAddress = MailingAddress;
        }

        if (RemitToAddress) {
          this.RemitToAddress = RemitToAddress;
        }

        Object.assign(this, { Company, Vendor, CompanyVendor });
        this.cdr.markForCheck();
      }
    });
  }

  viewSSN() {
    if (!this.LoggedInUser) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing user information.',
        { returnError: true }
      );
    }

    const { UserAccountRole: { role } = {} } = this.LoggedInUser;

    if (!role) {
      throw this.ToastService.addErrorToast('Error', 'Missing user role.', {
        returnError: true
      });
    }

    if (new Set(this.fullSSNPermissions).has(role)) {
      return this.VendorService.getFullVendorSSN(
        this.UtilityService.stringifyObjectsInQueryObject({
          id: this.VendorId,
          attributes: ['id', 'ssn']
        })
      ).subscribe({
        next: (Vendor) => {
          if (!Vendor) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Could not find Vendor.',
              { returnError: true }
            );
          }

          const { ssn } = Vendor;

          if (!ssn) {
            throw this.ToastService.addErrorToast('Error', 'Missing ssn.', {
              returnError: true
            });
          }

          Object.assign(this, { fullSSN: ssn });
          this.cdr.markForCheck();
        }
      });
    }

    if (new Set(this.maskedSSNPermissions).has(role)) {
      return this.VendorService.getMaskedVendorSSN(
        this.UtilityService.stringifyObjectsInQueryObject({
          id: this.VendorId,
          attributes: ['id'],
          aliasedAttributes: ['maskedSSN']
        })
      ).subscribe({
        next: (Vendor) => {
          if (!Vendor) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Could not find Vendor.',
              { returnError: true }
            );
          }

          const { maskedSSN } = Vendor;
          Object.assign(this, { maskedSSN });
          this.cdr.markForCheck();
        }
      });
    }

    return;
  }

  initializeClientVendorInformation(CompanyVendor: CompanyVendor) {
    const { country, Company, Vendor, allowEinAutoLink } = CompanyVendor;

    if (['undefined', 'null'].includes(typeof allowEinAutoLink)) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Client/Vendor data is missing auto-link information',
        { returnError: true }
      );
    }

    if (!country) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Client/Vendor data is missing country information.',
        { returnError: true }
      );
    }

    if (!Company) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Client information.',
        { returnError: true }
      );
    }

    if (!Vendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Vendor information.',
        { returnError: true }
      );
    }

    const { ComplianceContact, StreetAddress, MailingAddress, RemitToAddress } =
      Vendor;

    if (ComplianceContact) {
      this.ComplianceContact = ComplianceContact;
    }

    if (StreetAddress) {
      this.StreetAddress = StreetAddress;
    }

    if (MailingAddress) {
      this.MailingAddress = MailingAddress;
    }

    if (RemitToAddress) {
      this.RemitToAddress = RemitToAddress;
    }

    Object.assign(this, { Company, Vendor });
  }

  ngOnChanges(SimpleChanges: SimpleChanges): void {
    const { CurrentComplianceStep, CompanyVendor } = SimpleChanges;

    if (CurrentComplianceStep) {
      this.CanadianWorkersCompensationTypes.splice(0);
      const { currentValue } = CurrentComplianceStep;
      if (currentValue === 'Insurance') {
        if (this.SharedOperationStates.has('ON')) {
          this.CanadianWorkersCompensationTypes.push('WSIB');
        }

        if (this.SharedOperationStates.has('AB')) {
          this.CanadianWorkersCompensationTypes.push('WCB');
        }
      }
    }

    if (CompanyVendor) {
      const { currentValue } = CompanyVendor;
      this.initializeClientVendorInformation(currentValue);
    }
  }

  ngOnInit(): void {
    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Client Id.', {
        returnError: true
      });
    }

    if (!this.VendorId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
        returnError: true
      });
    }

    if (!this.SharedOperationStates) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Shared operation states.',
        { returnError: true }
      );
    }

    if (this.CompanyVendor) {
      this.initializeClientVendorInformation(this.CompanyVendor);
    } else {
      this.loadCompanyVendorData();
    }

    this.SharedDataService.listenForEvent<ViveProgress>().subscribe({
      next: ({ eventName, data }) => {
        if (
          new Set([
            'SetComplianceProgress',
            'SetComplianceProgressAfterUpdate'
          ]).has(eventName)
        ) {
          this.CurrentComplianceProgress = data;
          this.cdr.markForCheck();
        }
      }
    });

    if (this.CurrentComplianceStep) {
      this.SharedDataService.sendEvent(
        'GetComplianceProgress',
        new ViveProgress({ type: this.CurrentComplianceStep })
      );
    }
  }
}
